// ── Errorzen FE copy map ──────────────────────────────────────────────────────
// Localized title/description per KNOWN error code. Copy is resolved centrally
// here (via toErrorPresentation) and is never hardcoded per code at a call
// site. The base `en` map is typed total over KnownErrorCode so a registry
// regeneration (ASC-6328) that adds a code fails compilation here until copy
// is authored — the wire `title` (registry English) remains the runtime
// fallback for partial non-`en` locales. Spec: UI Specs: errorzen v2.1
// §Localization (FR-027, Epic ASC-6291).

import type { KnownErrorCode } from '@platform/errorzen-contracts'

export interface ErrorCopy {
  title: string
  description: string
}

/** Rule-3 copy — unvalidated legacy bodies and plain Errors are NEVER shown verbatim. */
export const GENERIC_ERROR_COPY: ErrorCopy = {
  title: 'Something needs attention',
  description: "We couldn't complete this. Please try again.",
}

const EN_COPY: Record<KnownErrorCode, ErrorCopy> = {
  'AUTH.TOKEN_MISSING': {
    title: 'Sign in required',
    description: 'Your session could not be verified. Please sign in again.',
  },
  'AUTH.TOKEN_EXPIRED': {
    title: 'Session expired',
    description: 'Your session has expired. Please sign in again.',
  },
  'AUTH.INVALID_CREDENTIALS': {
    title: 'Sign-in failed',
    description: 'The credentials provided were not accepted. Please sign in again.',
  },
  // A service-to-service credential problem, not a user one — the copy points at
  // the deployment rather than telling the user to sign in again (signing in
  // cannot fix a missing machine secret). Ref: ASC-6441.
  'AUTH.SERVICE_TOKEN_UNAVAILABLE': {
    title: 'Service temporarily unavailable',
    description:
      'A background service could not authenticate. This is a configuration issue on our side — please retry shortly or contact your admin.',
  },
  'AUTH.UNAUTHENTICATED': {
    title: 'Sign in required',
    description: 'Your session could not be verified. Please sign in again.',
  },
  'AUTH.FORBIDDEN': {
    title: 'Access restricted',
    description:
      "You don't have permission to perform this action. Contact your admin if you need access.",
  },
  'ACCESS.PERMISSION_DENIED': {
    title: 'Access restricted',
    description:
      "You don't have permission to perform this action. Contact your admin if you need access.",
  },
  'ACCESS.ORG_SCOPE_VIOLATION': {
    title: 'Access restricted',
    description: 'This item belongs to a different organization.',
  },
  'ACCESS.WORKSPACE_NOT_ALLOWED': {
    title: 'Access restricted',
    description: "You don't have access to this workspace. Contact your admin if you need access.",
  },
  'VALIDATION.MALFORMED_REQUEST': {
    title: "Request couldn't be processed",
    description: 'The request was malformed. Please review your input and try again.',
  },
  'VALIDATION.REQUEST_INVALID': {
    title: 'Check the highlighted fields',
    description: 'Some of the submitted values are invalid. Please review and resubmit.',
  },
  'VALIDATION.SCHEMA_MISMATCH': {
    title: "Request couldn't be processed",
    description:
      "The submitted data doesn't match the expected format. Please review and resubmit.",
  },
  'VALIDATION.INVALID_WEIGHTS': {
    title: 'Check the weights',
    description: 'The supplied weights are invalid. Please review and resubmit.',
  },
  'VALIDATION.PAYLOAD_TOO_LARGE': {
    title: 'Upload too large',
    description: 'The submitted data exceeds the allowed size. Reduce it and try again.',
  },
  'VALIDATION.OBJECTIVE_NOT_RESEARCHABLE': {
    title: "Couldn't start research",
    // Says what to CHANGE, not "try again": the relevancy gate judged this
    // objective unresearchable, so resubmitting it unchanged can only fail again.
    description:
      "That doesn't read as a research objective for this category. Describe what you want to find out — cost drivers, the supplier landscape, market dynamics, risks — and the agent will plan a study around it.",
  },
  // llmrouterzen capability enforcement (FR-168). Ref: ASC-10924.
  'VALIDATION.MODEL_CAPABILITY_MISSING': {
    title: 'Model not suitable',
    description:
      'The model mapped for this agent cannot handle this request (for example, it cannot read images). Map a model with that capability in Model Mapping.',
  },
  // llmrouterzen modality-correct routing (FR-017). Ref: ASC-11257.
  'LLM.MODEL_CLASS_ENDPOINT_MISMATCH': {
    title: 'Model type not supported',
    description:
      "This step uses a model type its service can't call. Contact your platform administrator.",
  },
  // llmrouterzen model-class enforcement (FR-045). Ref: ASC-11307.
  'LLM.MODEL_CLASS_NOT_COVERED': {
    title: 'No model available',
    description:
      "This step needs a type of model your organization hasn't enabled or assigned. Contact your platform administrator.",
  },
  'LLM.NO_EVALUATION_MODEL_AVAILABLE': {
    title: 'No evaluation model available',
    description:
      'This step needs an evaluation model, and none is enabled and assigned. Contact your platform administrator.',
  },
  'LLM.MODEL_CLASS_STRANDED': {
    title: 'Change not allowed',
    description:
      'This change would leave some steps without a model they need. Assign another model first, then try again.',
  },
  'LLM.MODEL_OUT_OF_CLASS': {
    title: 'Model not suitable',
    description:
      "That model doesn't fit the type this step requires. Choose a model of the required type.",
  },
  'VALIDATION.UNSUPPORTED_MEDIA_TYPE': {
    title: 'Unsupported format',
    description: "This file or content type isn't supported here.",
  },
  'RESOURCE.NOT_FOUND': {
    title: 'Not found',
    description: "We couldn't find what you're looking for. It may have been moved or deleted.",
  },
  'RESOURCE.METHOD_NOT_ALLOWED': {
    title: 'Action not available',
    description: "This action isn't available for this resource.",
  },
  'RESOURCE.CONFLICT': {
    title: 'Conflicting change',
    description: 'This item was changed elsewhere. Refresh and try again.',
  },
  // feedbackzen shadow item (FR-013/FR-145). Ref: ASC-10755.
  'RESOURCE.NOT_FORWARDABLE': {
    title: 'Recorded only',
    description:
      'This proposal was recorded while prompt-change proposals were turned off, so it cannot be sent for approval. Reject it to close it.',
  },
  // partner-assessment (Epic ASC-10167). Titles = registry English (error_codes.json);
  // descriptions follow each code's registry description, or UI Specs wording where the
  // spec gives it (NO_DEFAULT_FRAMEWORK, CHECKLIST_SUGGESTION_STALE). Ref: ASC-10178.
  'PPA.ARCHIVE_REJECTED': {
    title: 'The uploaded archive was rejected',
    description: 'The ZIP is over the limits, contains another archive, or cannot be read.',
  },
  'PPA.CHECKLIST_SUGGESTION_STALE': {
    title: 'The checklist suggestion is stale',
    description: 'Suggestion updated — review again.',
  },
  'PPA.CONFLICT_ALREADY_RESOLVED': {
    title: 'The conflict is already resolved',
    description: 'This conflict was resolved already. Reload to see the resolution.',
  },
  'PPA.FACT_SUPERSEDED': {
    title: 'The fact has been superseded',
    description: 'A newer version of this fact exists. Reload to see it.',
  },
  'PPA.FILE_TOO_LARGE': {
    title: 'The uploaded file exceeds the upload size limit',
    description: 'Choose a smaller file and upload it again.',
  },
  'PPA.INGESTION_RUNNING': {
    title: 'An ingestion run is already active',
    description: 'Wait for the current run to finish, then start again.',
  },
  'PPA.INVALID_RESULT_FOR_DIMENSION': {
    title: 'The result is not valid for this dimension',
    description: "Choose a result from this dimension's rating vocabulary.",
  },
  'PPA.INVALID_STATUS_TRANSITION': {
    title: 'The assessment status change is not allowed',
    description: 'This assessment cannot move to that status from its current one.',
  },
  'PPA.NO_DEFAULT_FRAMEWORK': {
    title: 'No default published framework version exists',
    description: 'No framework loaded — ask your configuration owner.',
  },
  'PPA.NO_SOURCES': {
    title: 'No sources are configured for ingestion',
    description: 'Add at least one source, then start ingestion.',
  },
  'PPA.ORGANIZATION_ARCHIVED': {
    title: 'The organization is archived',
    description: 'Choose an active organization for a new assessment.',
  },
  'PPA.VERSION_IMMUTABLE': {
    title: 'The published version cannot be edited',
    description: 'Create a new draft from it to make changes.',
  },
  'PPA.VERSION_NOT_PUBLISHED': {
    title: 'The version is not published',
    description: 'Publish this version before making it the default.',
  },
  'PPA.WORK_PRODUCT_APPROVED': {
    title: 'The work product is approved and cannot be edited',
    description: 'Approved content stays as it was approved.',
  },
  'PPA.WORK_PRODUCT_NOT_APPROVED': {
    title: 'The work product is not approved',
    description: 'Approve the work product before exporting it.',
  },
  'RESOURCE.ALREADY_EXISTS': {
    title: 'Name already in use',
    description: 'Something with this name already exists. Pick a different name.',
  },
  'SESSION.CONFIRM_TOKEN_EXPIRED': {
    title: 'Confirmation expired',
    // Says what to DO, and deliberately never "try again": the token is consumed or
    // past its 15-minute TTL, so resubmitting the same confirmation can only fail.
    // The user has to re-issue the request, which is a new action, not a retry.
    description:
      'This confirmation is no longer valid — it was already used, or it sat for more than 15 minutes. Nothing was changed. Ask again to get a fresh confirmation.',
  },
  'RESOURCE.RATE_LIMITED': {
    title: 'Too many requests',
    description: "You're sending requests faster than allowed. Wait a moment and try again.",
  },
  'LLM.PROVIDER_AUTH_FAILED': {
    title: 'AI provider unavailable',
    description: 'The AI provider rejected the configured credentials. Contact your admin.',
  },
  'LLM.PROVIDER_QUOTA_EXHAUSTED': {
    title: 'AI provider out of credit',
    description: 'The provider account is out of credit. Contact your admin.',
  },
  'LLM.PROVIDER_RATE_LIMITED': {
    title: 'AI provider busy',
    description: 'The AI provider is rate limiting requests. Wait a moment and try again.',
  },
  'LLM.CALL_TIMEOUT': {
    title: 'The request took too long',
    description: 'This took longer than expected and was stopped. Please try again.',
  },
  'LLM.PROVIDER_TIMEOUT': {
    title: 'AI provider timed out',
    description: 'The AI provider took too long to respond. Please try again.',
  },
  'LLM.PROVIDER_UNREACHABLE': {
    title: 'AI provider unreachable',
    description: "We couldn't reach the AI provider. Please try again.",
  },
  // Deliberately NOT "please try again" — unlike PROVIDER_UNREACHABLE above,
  // this endpoint is one we were configured with, so retrying cannot fix it.
  // Ref: ASC-7588.
  'LLM.PROVIDER_ENDPOINT_MISCONFIGURED': {
    title: 'AI provider misconfigured',
    description:
      "The endpoint configured for this AI provider couldn't be reached. Contact your admin.",
  },
  'LLM.CONTEXT_TOO_LARGE': {
    title: 'Request too large for the model',
    description: "This request exceeds the model's context limit. Try a smaller request.",
  },
  'LLM.PROVIDER_BAD_REQUEST': {
    title: 'AI request rejected',
    description: 'The AI provider rejected this request. Please try again or contact your admin.',
  },
  // The two output-shape failures, deliberately worded to give OPPOSITE advice —
  // that difference is the whole point of splitting them. An empty completion is
  // deterministic for a given model + budget, so telling the operator to retry sent
  // them into a 30-40s loop that could not succeed (ASC-8420); malformed content can
  // genuinely come good on a re-roll. Ref: ASC-8440.
  'LLM.OUTPUT_EMPTY': {
    title: 'The model returned nothing',
    description:
      'The model used its entire output budget before writing a response. Retrying will fail the same way — ask an admin to assign a model with a larger output budget, or simplify the request.',
  },
  'LLM.OUTPUT_UNPARSEABLE': {
    title: "The model's response could not be read",
    description:
      'The model replied in a format we could not read. Try again — if it keeps happening, ask an admin to check which model is assigned.',
  },
  'LLM.DISPATCH_EXHAUSTED': {
    title: 'AI temporarily unavailable',
    description: 'All configured AI routes failed. Wait a moment and try again.',
  },
  'LLM.BUDGET_EXHAUSTED': {
    title: 'AI budget exhausted',
    description: 'The AI usage budget for your organization is exhausted. Contact your admin.',
  },
  'LLM.MODEL_KILL_SWITCHED': {
    title: 'Model disabled',
    description: 'This model has been disabled by an administrator.',
  },
  'LLM.KEY_NOT_CONFIGURED': {
    title: 'AI provider not configured',
    description: 'No API key is configured for the AI provider. Contact your admin.',
  },
  // Deliberately does NOT say "contact your admin" like KEY_NOT_CONFIGURED above:
  // nothing is misconfigured here, the key store was unreachable. Ref: ASC-7431
  'LLM.KEY_LOOKUP_UNAVAILABLE': {
    title: 'AI provider check unavailable',
    description:
      'We could not reach the credential store to verify the AI provider. Try again shortly.',
  },
  'LLM.PROVIDER_DISABLED': {
    title: 'AI provider disabled',
    description: 'This AI provider is disabled for your organization. Contact your admin.',
  },
  'LLM.MODEL_NOT_ASSIGNED': {
    title: 'Model not assigned',
    description: 'No model is assigned for this task. Contact your admin.',
  },
  'LLM.MODEL_NOT_IN_CATALOG': {
    title: 'Model not available',
    description: "The requested model isn't in the catalog. Contact your admin.",
  },
  'LLM.MODEL_NOT_IN_ORG_ALLOWLIST': {
    title: 'Model not allowed',
    description: "This model isn't in your organization's allowlist. Contact your admin.",
  },
  'LLM.MODEL_DISABLED': {
    title: 'Model disabled',
    description: 'This model is disabled for your organization. Contact your admin.',
  },
  'LLM.PARAM_REJECTED': {
    title: 'AI request rejected',
    description: 'The model rejected one of the request parameters. Contact your admin.',
  },
  'LLM.PROVIDER_NOT_FOUND': {
    title: 'AI provider not found',
    description: 'The configured AI provider could not be found. Contact your admin.',
  },
  'LLM.MODEL_NOT_FOUND': {
    title: 'Model not found',
    description: 'The requested model could not be found. Contact your admin.',
  },
  'LLM.AGENT_NOT_FOUND': {
    title: 'Agent not found',
    description: 'The requested agent could not be found. Contact your admin.',
  },
  'LLM.PROVIDER_VALIDATION_FAILED': {
    title: 'AI provider error',
    description: 'The AI provider returned an unexpected response. Please try again.',
  },
  'LLM.MODEL_NOT_EMBEDDING': {
    title: 'Model not usable for embeddings',
    description: 'The requested model cannot generate embeddings. Contact your admin.',
  },
  'LLM.DIMENSIONS_NOT_CONFIGURABLE': {
    title: 'Fixed-dimension model',
    description: "This model's output size can't be customized. Contact your admin.",
  },
  'LLM.NO_EMBEDDING_MODEL_AVAILABLE': {
    title: 'No embedding model available',
    description: 'No embedding model is available for your organization. Contact your admin.',
  },
  'LLM.UPSTREAM_FAILED': {
    title: 'AI provider error',
    description: 'The embedding provider call failed. Please try again.',
  },
  'LLM.UPSTREAM_TIMEOUT': {
    title: 'AI provider timed out',
    description: 'The embedding provider took too long to respond. Please try again.',
  },
  'UPSTREAM.UNAVAILABLE': {
    title: 'Service unavailable',
    description: 'An upstream service is currently unavailable. Please try again.',
  },
  'UPSTREAM.BAD_RESPONSE': {
    title: 'Unexpected response',
    description: 'An upstream service returned an unexpected response. Please try again.',
  },
  'UPSTREAM.TIMEOUT': {
    title: 'Request timed out',
    description: 'An upstream service took too long to respond. Please try again.',
  },
  // Separate from DATA.QUERY_FAILED because the advice differs: a timeout may clear
  // on its own, a rejected query never will. Ref: ASC-8440.
  'DATA.QUERY_TIMEOUT': {
    title: 'The query took too long',
    description:
      'This query did not finish in time. Try narrowing the date range or filters, then run it again.',
  },
  'DATA.QUERY_FAILED': {
    title: 'Query failed',
    description: "We couldn't run this query. Please try again.",
  },
  'DATA.PIPELINE_INPUT_INVALID': {
    title: 'Pipeline input invalid',
    description: 'The pipeline input failed validation. Review the input and try again.',
  },
  'DATA.PIPELINE_STAGE_CRASHED': {
    title: 'Pipeline stage failed',
    description: 'A pipeline stage failed unexpectedly. Please try again or contact support.',
  },
  'DATA.BACKFILL_CONFIRMATION_REQUIRED': {
    // Generic on purpose. This is not a failure — it is an unanswered question, and
    // the real wording depends on `details.reason` (fill an empty mart vs REPLACE the
    // rows of a populated one), which only BackfillConfirmDialog knows. This copy is
    // the fallback for any surface that renders the code without that dialog.
    title: 'Confirm whether to backfill existing data',
    description:
      'Activating this profile can compose already-ingested data into its mart. Choose whether to backfill now or activate forward-only.',
  },
  'DATA.EXTRACTION_PROFILE_CONFLICT': {
    title: 'That gold mart is already taken',
    description:
      'Another live extraction profile already serves this mart. Deprecate that profile, or choose a different mart.',
  },
  'DATA.SCOPE_INVALID': {
    title: "That data scope can't be resolved",
    description:
      'Check the columns and selectors it names, then try again. The message says which part could not be resolved.',
  },
  'CONFIG.SECRET_MISSING': {
    title: 'Configuration incomplete',
    description: 'A required secret is not configured. Contact your admin.',
  },
  'CONFIG.PROVIDER_CREDENTIALS_INVALID': {
    title: 'Credentials invalid',
    description: 'The configured provider credentials were rejected. Contact your admin.',
  },
  'CONFIG.ORG_NOT_PROVISIONED': {
    title: 'Organization not provisioned',
    description: "Your organization isn't fully provisioned yet. Contact your admin.",
  },
  'CONFIG.ORG_PROVISIONING_INCOMPLETE': {
    title: 'Setup not finished',
    description:
      "This organization isn't fully set up yet. Run Sync provisioning on it, then try again.",
  },
  'CONFIG.ORG_PROVISIONING_FAILED': {
    title: 'Provisioning failed',
    description:
      'Setting up this organization failed partway through. Review the failed step and retry.',
  },
  // ── evalzen (Epic ASC-4285) ──────────────────────────────────────────────
  // Audience is the platform/data admin tier, not an end user: evalzen's screens
  // are behind `evalzen:dataset` / `evalzen:run`, so the copy names the concrete
  // next action rather than "contact your admin".
  'EVALZEN.FILTER_REQUIRED': {
    title: 'Pick an agent or a tool',
    description: 'Quality results are scoped to one agent or one tool. Choose one to continue.',
  },
  'EVALZEN.TOOL_UNKNOWN': {
    title: 'No evals for this tool',
    description:
      'This tool has no eval surface yet. A tool gets one when an `evals/tool.yaml` ships beside it.',
  },
  'EVALZEN.SUITE_NOT_FOUND': {
    title: 'Suite not found',
    description:
      'This eval suite does not exist in your organization. It may have been renamed or removed.',
  },
  'EVALZEN.SUITE_EXISTS': {
    title: 'That suite name is taken',
    description: 'This tool already has a suite with that name. Choose a different name.',
  },
  'EVALZEN.SUITE_EMPTY': {
    title: 'Add a case first',
    description: 'A suite needs at least one active case before it can run.',
  },
  'EVALZEN.SUITE_ARCHIVED': {
    title: 'This suite is archived',
    description:
      'An archived suite is read-only and cannot run. Unarchive it to edit its cases, thresholds or baseline.',
  },
  'EVALZEN.ORG_AGENT_SUITE_UNSUPPORTED': {
    title: "This agent suite can't run yet",
    description:
      'Suites your organization creates for an agent can hold and edit cases, but cannot be run, re-thresholded or baselined yet.',
  },
  'EVALZEN.PLATFORM_SUITE_READONLY': {
    title: 'Platform suite is read-only',
    description:
      'Cases in a platform baseline suite ship with the product. Copy it to your own suite to edit them.',
  },
  'EVALZEN.CASE_EXISTS': {
    title: 'That case id is taken',
    description:
      'A case id must be unique across all your suites. Choose a different id, or leave it blank to get a generated one.',
  },
  'EVALZEN.CASE_NOT_FOUND': {
    title: 'Case not found',
    description: 'This suite has no case with that id. Refresh to see its current cases.',
  },
  'EVALZEN.PIN_INVALID': {
    title: "Pin doesn't fit this tool",
    description: "A pin value isn't allowed by the tool's pin schema. The message says which pin.",
  },
  'EVALZEN.INPUT_INVALID': {
    title: "Input doesn't match the tool",
    description: "The case input failed the tool's input schema. The message says which field.",
  },
  'EVALZEN.PLATFORM_ONLY_SUITE': {
    title: 'Runs on the platform tenant only',
    description:
      'This tool is marked platform-only, so its suites cannot be run from your organization.',
  },
  'EVALZEN.EXPECTED_OUTPUT_INVALID': {
    title: "Expected output doesn't match the tool",
    description:
      "The expected output failed the tool's output schema. The message says which field.",
  },
  'EVALZEN.RUN_NOT_FOUND': {
    title: 'Run not found',
    description: 'No run by that name exists for this suite.',
  },
  'EVALZEN.RUN_IN_PROGRESS': {
    title: 'A run is already going',
    description:
      'This suite already has a run queued or in progress. Wait for it to finish, then start another.',
  },
  'EVALZEN.JOB_NOT_FOUND': {
    title: 'Job not found',
    description: 'This eval job no longer exists. Refresh to see the current runs.',
  },
  'EVALZEN.NO_BASELINE': {
    title: 'No baseline to compare against',
    description:
      'Comparison needs a promoted baseline. Promote a passing run first — the most recent run is never used automatically.',
  },
  'EVALZEN.BASELINE_RUN_INVALID': {
    title: "That run can't be a baseline",
    description:
      'A baseline must be a complete run of the suite as it stands now. The message says why this one is not — re-run the suite, then promote that run.',
  },
  'EVALZEN.THRESHOLDS_INVALID': {
    title: 'Thresholds are not valid',
    description:
      'A threshold value is out of range, loosens a committed gate, or names a dimension this tool does not score. The message says which field and what is allowed.',
  },
  'EVALZEN.LANGFUSE_UNAVAILABLE': {
    title: 'Evaluation backend unreachable',
    description: 'Langfuse could not be reached. Please try again shortly.',
  },
  'EVALZEN.CONFIG_MISSING': {
    title: 'Evaluation not configured',
    description: 'Your organization has no Langfuse credentials yet. Contact your admin.',
  },
  'EVALZEN.CANDIDATE_NOT_SERVED': {
    title: 'Candidate prompt not served',
    description:
      'This case was served a different prompt version than the one under test, so it was not scored.',
  },
  'EVALZEN.CANDIDATE_NOT_EXERCISED': {
    title: 'Candidate prompt not exercised',
    description:
      'No case in this suite used the prompt under test, so the run says nothing about it.',
  },
  'EVALZEN.TOOL_COST_CEILING': {
    title: 'Tool spend ceiling reached',
    description:
      'This run stopped early because its tools reached the suite spend ceiling. It cannot be promoted.',
  },
  'EVALZEN.TIER_CAP_EXCEEDED': {
    title: 'Case went past its cost tier',
    description:
      'This case was capped to the triage tier but reached a web tier, so it was not scored.',
  },
  'EVALZEN.RUNNER_NOT_PROVISIONED': {
    title: 'Evaluation runner not set up',
    description:
      'The evaluation service account is not a member of this organization, so agent suites cannot run here.',
  },
  'EVALZEN.EVAL_WORKSPACE_UNRESOLVED': {
    title: 'No evaluation workspace',
    description:
      'Mark one workspace for evaluation, with the agent enabled there, before running an agent suite.',
  },
  'EVALZEN.AGENT_UNAVAILABLE': {
    title: 'Agent not available to evaluate',
    description:
      'Your organization is not subscribed to this agent, or its data has no rows yet, so the agent suite cannot run here.',
  },
  'EVALZEN.TAXONOMY_NOT_CONFIGURED': {
    title: 'Taxonomy not configured',
    description:
      'A case in this suite pins a taxonomy your organization has not uploaded. Upload it in Context Studio, or change the pin, then run again.',
  },
  'EVALZEN.REQUIREMENT_NOT_MET': {
    title: 'Data not provisioned',
    description:
      'This suite needs a gold mart your organization does not have yet. It becomes runnable once that data is ingested.',
  },
  'EVALZEN.RUNNER_FORBIDDEN': {
    title: 'Agent refused the evaluation runner',
    description:
      'The agent rejected the evaluation service account, so nothing was scored. Check its access to the agent.',
  },
  'EVALZEN.AGENT_TIMEOUT': {
    title: 'Agent took too long',
    description:
      'The agent did not answer this case within the case timeout, so it was not scored.',
  },
  'EVALZEN.TRACE_UNAVAILABLE': {
    title: 'Trace not available',
    description:
      "The agent's trace for this case could not be read, so it was not scored. Try the run again.",
  },
  'EVALZEN.GATE_IN_PROGRESS': {
    title: 'Test already running',
    description: 'A test for this proposal is already in progress. Wait for it to finish.',
  },
  'EVALZEN.GATE_NOT_PASSED': {
    title: 'Test has not passed',
    description: 'Run the test and wait for it to pass before approving this proposal.',
  },
  'EVALZEN.GATE_NOT_COVERED_UNACKNOWLEDGED': {
    title: 'No test coverage',
    description:
      'No eval case covers this prompt. Acknowledge that it ships untested to approve it.',
  },
  'EVALZEN.REVIEW_NOT_ENABLED': {
    title: 'Trace review is off',
    description:
      'Trace review is not enabled for your organization. A platform admin turns it on per organization.',
  },
  'EVALZEN.REVIEW_QUEUE_MISSING': {
    title: 'No review queue yet',
    description: 'This subject has no review queue. Set up review queues, then try again.',
  },
  'EVALZEN.REVIEW_UNSUPPORTED': {
    title: "Review isn't supported here",
    description: "This subject can't turn reviewed traces into cases yet.",
  },
  'EVALZEN.REVIEW_CONFIG_CONFLICT': {
    title: 'Score config conflict',
    description:
      'A score config with the same name already exists in Langfuse with other categories. Rename or archive it, then set up review queues again.',
  },
  'EVALZEN.IMPORT_IN_PROGRESS': {
    title: 'An import is already going',
    description:
      'This suite already has an import of reviewed traces in progress. Wait for it to finish, then start another.',
  },
  'INTERNAL.CACHE_UNAVAILABLE': {
    title: 'Temporary issue',
    description: 'A backing service is temporarily unavailable. Please try again.',
  },
  'INTERNAL.UNAVAILABLE': {
    title: 'Service unavailable',
    description: 'The service is temporarily unavailable. Please try again.',
  },
  'INTERNAL.UNHANDLED': {
    // "later", not the bare "Please try again." the other 13 descriptions use: an
    // unhandled 5xx is a server-side fault, so an immediate retry reproduces it.
    // Scoped to this code deliberately — the siblings cover cases where retrying now
    // IS reasonable (a timeout, a transient provider call). Ref: ASC-9396.
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again later.',
  },
  'ZENVECTOR.MODEL_NOT_EMBEDDING': {
    title: 'The target model is not an embedding model',
    description: 'Choose an embedding model as the migration target.',
  },
  'ZENVECTOR.NAMESPACE_NOT_FOUND': {
    title: 'This vector namespace is not registered',
    description: 'The requested namespace could not be found in the registry.',
  },
  'ZENVECTOR.MIGRATION_STATE_INVALID': {
    title: 'This migration action is not valid in the current state',
    description: 'Refresh to see the current migration state and try again.',
  },
  'ZENVECTOR.MIGRATION_IN_PROGRESS': {
    title: 'A migration is already in progress for this namespace',
    description: 'Wait for the current migration to finish before starting another.',
  },
  'ZENVECTOR.DIMENSION_MISMATCH': {
    title: 'The target model cannot produce the requested dimension',
    description: 'Choose a model whose supported dimensions match this namespace.',
  },
}

// Non-`en` locale maps may be partial — `en` is the guaranteed fallback.
const COPY_BY_LOCALE: Record<string, Partial<Record<KnownErrorCode, ErrorCopy>>> = {
  en: EN_COPY,
}

/**
 * Resolve the FE copy for a known code. Locale is matched on its primary
 * subtag (`en-US` -> `en`); unknown locales and partial locale maps fall back
 * to the total `en` map, so this always resolves.
 */
export function resolveErrorCopy(code: KnownErrorCode, locale?: string): ErrorCopy {
  const primarySubtag = (locale ?? 'en').toLowerCase().split('-')[0]
  return COPY_BY_LOCALE[primarySubtag]?.[code] ?? EN_COPY[code]
}
